import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { ProjectPhase, ProjectProduct, Ticket } from '@catalyst-psa/shared';

import { useAuth } from '../../auth/authContext.js';
import { useDetailOverlay } from '../../components/layout/DetailOverlay.js';
import { PageHeader } from '../../components/layout/PageHeader.js';
import { useTrackRecent } from '../quick-access/useTrackRecent.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { DataState } from '../../components/ui/DataState.js';
import { Icon } from '../../components/ui/Icon.js';
import { TabNav } from '../../components/ui/TabNav.js';
import { HistoryPanel } from '../audit/HistoryPanel.js';
import { formatDate } from '../../utils/date.js';
import { useBackNavigation } from '../../utils/navigation.js';
import { useProducts } from '../products/api.js';
import { formatProductLabel } from '../products/format.js';
import { useTickets, useTicketStatusOptions, useAssignableMembers } from '../tickets/api.js';
import { TicketFormModal } from '../tickets/TicketFormModal.js';
import { ProjectFormModal } from './ProjectFormModal.js';
import { PhaseFormModal } from './PhaseFormModal.js';
import { ProjectInvoicesTab } from './ProjectInvoicesTab.js';
import { ProjectTimeEntriesTab } from './ProjectTimeEntriesTab.js';
import { ProjectProductFormModal } from './ProjectProductFormModal.js';
import {
  useCloseProjectReadyToBill,
  useProject,
  useProjectPhases,
  useProjectProducts,
  useRecomputeProjectPhaseRates,
  useRegenerateProjectPublicLink,
} from './api.js';
import { PROJECT_STATUS_LABELS, PROJECT_STATUS_TONES } from './status.js';

const PHASE_STATUS_TONES: Record<string, 'green' | 'blue'> = {
  active: 'green',
  completed: 'blue',
};

type ProjectTab = 'details' | 'products' | 'time' | 'invoices' | 'public' | 'history';

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

function formatMoney(value: number): string {
  return currency.format(value);
}

function lineTotal(product: ProjectProduct): number {
  return Number(product.quantity) * Number(product.unitPrice);
}

export function ProjectDetailPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can, isAdmin } = useAuth();
  const canEdit = can('projects', 'edit');
  const canViewInvoices = can('invoices', 'view');
  const canViewTime = can('time', 'view');
  const canEditTickets = can('projectTickets', 'edit');
  const backNav = useBackNavigation();
  const { inOverlay } = useDetailOverlay();

  const projectQuery = useProject(id);
  const phasesQuery = useProjectPhases(id);
  const productsQuery = useProjectProducts(id);
  const ticketsQuery = useTickets({ page: 1, pageSize: 200, projectId: id });
  const statusesQuery = useTicketStatusOptions();
  const membersQuery = useAssignableMembers();
  const catalogQuery = useProducts({ page: 1, pageSize: 200 });

  const [editOpen, setEditOpen] = useState(false);
  const [phaseModalOpen, setPhaseModalOpen] = useState(false);
  const [editingPhase, setEditingPhase] = useState<ProjectPhase | null>(null);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<ProjectProduct | null>(null);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [editingTicket, setEditingTicket] = useState<Ticket | null>(null);
  const [ticketInitialPhaseId, setTicketInitialPhaseId] = useState<string | null>(null);

  const [linkCopied, setLinkCopied] = useState(false);
  const [regenerateOpen, setRegenerateOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeNotice, setCloseNotice] = useState<{ tone: 'ok' | 'warn'; text: string } | null>(
    null,
  );
  const [tab, setTab] = useState<ProjectTab>('details');
  const regenerateLink = useRegenerateProjectPublicLink();
  const closeReadyToBill = useCloseProjectReadyToBill();

  const queryClient = useQueryClient();
  const recomputeRates = useRecomputeProjectPhaseRates(id);
  const [recomputeMsg, setRecomputeMsg] = useState<{ phaseId: string; text: string } | null>(null);

  const handleRecomputePhaseRates = (phaseId: string): void => {
    recomputeRates.mutate(phaseId, {
      onSuccess: (result) => {
        setRecomputeMsg({
          phaseId,
          text: `${result.updated} entr${result.updated === 1 ? 'y' : 'ies'} updated`,
        });
        void queryClient.invalidateQueries({ queryKey: ['time-entries'] });
        void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      },
    });
  };

  const project = projectQuery.data;
  useTrackRecent(
    project
      ? {
          entity: 'project',
          id: project.id,
          label: `#${project.number} · ${project.name}`,
          path: `/projects/${project.id}`,
        }
      : null,
  );
  const phases = useMemo(
    () => [...(phasesQuery.data?.data ?? [])].sort((a, b) => a.sequence - b.sequence),
    [phasesQuery.data],
  );
  const products = productsQuery.data?.data ?? [];
  const tickets = ticketsQuery.data?.data ?? [];

  const customerName = useMemo(() => project?.customerName ?? '—', [project]);

  const statusNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const status of statusesQuery.data ?? []) map.set(status.id, status.name);
    return map;
  }, [statusesQuery.data]);

  const memberNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const member of membersQuery.data ?? []) map.set(member.id, member.displayName);
    return map;
  }, [membersQuery.data]);

  const ticketsByPhase = useMemo(() => {
    const map = new Map<string | null, Ticket[]>();
    for (const ticket of tickets) {
      const key = ticket.phaseId ?? null;
      const list = map.get(key) ?? [];
      list.push(ticket);
      map.set(key, list);
    }
    return map;
  }, [tickets]);

  const unphasedTickets = ticketsByPhase.get(null) ?? [];
  const fixedFeeTotal = useMemo(
    () => products.reduce((sum, product) => sum + lineTotal(product), 0),
    [products],
  );
  const attachedProductIds = useMemo(() => products.map((p) => p.productId), [products]);
  const productLabels = useMemo(() => {
    const map = new Map<string, string>();
    for (const product of catalogQuery.data?.data ?? []) {
      map.set(product.id, formatProductLabel(product));
    }
    return map;
  }, [catalogQuery.data]);
  const nextSequence = useMemo(
    () => (phases.length ? Math.max(...phases.map((p) => p.sequence)) + 1 : 1),
    [phases],
  );

  const openCreatePhase = (): void => {
    setEditingPhase(null);
    setPhaseModalOpen(true);
  };
  const openEditPhase = (phase: ProjectPhase): void => {
    setEditingPhase(phase);
    setPhaseModalOpen(true);
  };
  const openCreateProduct = (): void => {
    setEditingProduct(null);
    setProductModalOpen(true);
  };
  const openEditProduct = (product: ProjectProduct): void => {
    setEditingProduct(product);
    setProductModalOpen(true);
  };
  const openEditTicket = (ticket: Ticket): void => {
    setEditingTicket(ticket);
    setTicketInitialPhaseId(null);
    setTicketModalOpen(true);
  };
  const openCreateTicket = (phaseId: string | null): void => {
    setEditingTicket(null);
    setTicketInitialPhaseId(phaseId);
    setTicketModalOpen(true);
  };

  const renderTicketRow = (ticket: Ticket): JSX.Element => {
    const assignees = ticket.assigneeMemberIds
      .map((memberId) => memberNames.get(memberId))
      .filter((name): name is string => Boolean(name));
    return (
      <button
        key={ticket.id}
        type="button"
        onClick={() => openEditTicket(ticket)}
        className="flex w-full items-center justify-between gap-3 px-4 py-2 text-left text-sm hover:bg-slate-50"
      >
        <span className="flex min-w-0 items-center gap-2">
          <span className="font-mono text-xs text-slate-500">#{ticket.number}</span>
          <span className="truncate text-slate-800">{ticket.title}</span>
        </span>
        <span className="flex shrink-0 items-center gap-3">
          <span className="text-xs text-slate-500">
            {assignees.length ? assignees.join(', ') : 'Unassigned'}
          </span>
          <span className="text-xs text-slate-400 tabular-nums">
            {ticket.budgetHours ?? '0'} budget hrs
          </span>
          <span className="text-xs text-slate-400 tabular-nums">
            {ticket.actualHours} actual hrs
          </span>
          <span className="text-xs text-slate-500">{statusNames.get(ticket.statusId) ?? '—'}</span>
        </span>
      </button>
    );
  };

  if (projectQuery.isLoading || projectQuery.isError || !project) {
    return (
      <div>
        <PageHeader title="Project" />
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <DataState
            isLoading={projectQuery.isLoading}
            isError={projectQuery.isError}
            error={projectQuery.error}
            onRetry={() => void projectQuery.refetch()}
            isEmpty={!projectQuery.isLoading && !project}
            emptyTitle="Project not found"
            emptyDescription="This project may have been deleted."
          >
            <div />
          </DataState>
        </div>
      </div>
    );
  }

  const publicUrl = project.publicToken ? `${window.location.origin}/p/${project.publicToken}` : '';

  return (
    <div>
      {backNav.canGoBack ? (
        <button
          onClick={backNav.goBack}
          className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
        >
          ← {backNav.backLabel}
        </button>
      ) : inOverlay ? null : (
        <Link
          to="/projects"
          className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
        >
          ← Back to projects
        </Link>
      )}

      <PageHeader
        title={project.name}
        description={`#${project.number} · ${customerName}`}
        actions={
          <div className="flex items-center gap-2">
            {project.customerId ? (
              <Button
                variant="secondary"
                onClick={() =>
                  navigate(`/customers?returnCustomerId=${project.customerId}&returnTab=projects`)
                }
              >
                Go to Customer
              </Button>
            ) : null}
            {canEdit ? (
              <>
                {project.status === 'active' ? (
                  <Button variant="secondary" onClick={() => setCloseOpen(true)}>
                    Close project &amp; mark Ready to Bill
                  </Button>
                ) : null}
                <Button variant="secondary" onClick={() => setEditOpen(true)}>
                  Edit project
                </Button>
              </>
            ) : null}
          </div>
        }
      />

      {closeNotice ? (
        <div
          className={`mb-4 flex items-start justify-between gap-3 rounded-lg px-3 py-2 text-sm ${
            closeNotice.tone === 'ok'
              ? 'bg-emerald-50 text-emerald-700'
              : 'bg-amber-50 text-amber-700'
          }`}
        >
          <span>{closeNotice.text}</span>
          <button type="button" onClick={() => setCloseNotice(null)} className="font-medium">
            Dismiss
          </button>
        </div>
      ) : null}

      <TabNav
        tabs={[
          { id: 'details', label: 'Details' },
          { id: 'products', label: 'Products (fixed fee)' },
          ...(canViewTime ? [{ id: 'time', label: 'Time entries' }] : []),
          ...(canViewInvoices ? [{ id: 'invoices', label: 'Invoices' }] : []),
          { id: 'public', label: 'Public link' },
          { id: 'history', label: 'History' },
        ]}
        activeId={tab}
        onChange={(id) => setTab(id as ProjectTab)}
      />

      {tab === 'history' ? <HistoryPanel entityType="Project" entityId={project.id} /> : null}

      {tab === 'time' ? <ProjectTimeEntriesTab projectId={project.id} /> : null}

      {tab === 'invoices' ? <ProjectInvoicesTab projectId={project.id} /> : null}

      <div>
        {/* Public portal link */}
        {tab === 'public' && !project.publicToken ? (
          <div className="mb-6 rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500 shadow-sm">
            No public link is available for this project yet. The public link is enabled while the
            project is active.
          </div>
        ) : null}
        {tab === 'public' && project.publicToken ? (
          <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-slate-800">Public project link</p>
                  {project.status !== 'active' ? (
                    <Badge tone="amber">Inactive — link disabled</Badge>
                  ) : null}
                </div>
                <p className="mt-1 truncate font-mono text-xs text-slate-500">{publicUrl}</p>
                <p className="mt-1 text-xs text-slate-400">
                  Anyone with this link can view project stats and add hours for assigned members.
                  {project.status !== 'active'
                    ? ' The link only works while the project is active.'
                    : ''}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Button
                  variant="secondary"
                  onClick={() => {
                    void navigator.clipboard.writeText(publicUrl).then(() => {
                      setLinkCopied(true);
                      window.setTimeout(() => setLinkCopied(false), 2000);
                    });
                  }}
                >
                  {linkCopied ? 'Copied!' : 'Copy link'}
                </Button>
                {canEdit ? (
                  <Button variant="ghost" onClick={() => setRegenerateOpen(true)}>
                    Regenerate
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}

        {tab === 'details' ? (
          <>
            {/* Overview */}
            <div className="mb-6 grid gap-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Status</p>
                <div className="mt-1">
                  <Badge tone={PROJECT_STATUS_TONES[project.status]}>
                    {PROJECT_STATUS_LABELS[project.status]}
                  </Badge>
                </div>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Billable</p>
                <p className="mt-1 text-sm text-slate-800">{project.isBillable ? 'Yes' : 'No'}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Project manager</p>
                <p className="mt-1 text-sm text-slate-800">{project.projectManagerName}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">
                  Default service category
                </p>
                <p className="mt-1 text-sm text-slate-800">{project.defaultServiceCategoryName}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">
                  Default billing class
                </p>
                <p className="mt-1 text-sm text-slate-800">{project.defaultBillingClassName}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Start date</p>
                <p className="mt-1 text-sm text-slate-800">{formatDate(project.startDate)}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">End date</p>
                <p className="mt-1 text-sm text-slate-800">{formatDate(project.endDate)}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Budget hours</p>
                <p className="mt-1 text-sm text-slate-800 tabular-nums">{project.budgetHours}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Actual hours</p>
                <p className="mt-1 text-sm text-slate-800 tabular-nums">{project.actualHours}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">Duration</p>
                <p className="mt-1 text-sm text-slate-800 tabular-nums">
                  {project.durationDays === null ? '—' : `${project.durationDays} days`}
                </p>
              </div>
              {project.opportunityId ? (
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">
                    Source opportunity
                  </p>
                  <Link
                    to={`/sales/opportunities/${project.opportunityId}`}
                    className="mt-1 block text-sm font-medium text-brand-600 hover:text-brand-700"
                  >
                    {project.opportunityName ?? 'Open opportunity'} →
                  </Link>
                </div>
              ) : null}
              {project.onboardingId ? (
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">Onboarding</p>
                  <Link
                    to={`/sales/onboarding/${project.onboardingId}`}
                    className="mt-1 block text-sm font-medium text-brand-600 hover:text-brand-700"
                  >
                    Open onboarding →
                  </Link>
                </div>
              ) : null}
              {project.quoteSowToken ? (
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">
                    SOW for delivery team
                  </p>
                  <a
                    href={`/sow/${project.quoteSowToken}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 block text-sm font-medium text-brand-600 hover:text-brand-700"
                  >
                    Open SOW ↗
                  </a>
                </div>
              ) : null}
              {project.cuListId ? (
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">CU project</p>
                  {project.cuListUrl ? (
                    <a
                      href={project.cuListUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 block text-sm font-medium text-brand-600 hover:text-brand-700"
                    >
                      {project.cuListName ?? 'Open ClickUp list'} →
                    </a>
                  ) : (
                    <p className="mt-1 text-sm text-slate-800">
                      {project.cuListName ?? project.cuListId}
                    </p>
                  )}
                </div>
              ) : null}
              {project.description ? (
                <div className="sm:col-span-2 lg:col-span-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Description</p>
                  <p className="mt-1 whitespace-pre-line text-sm text-slate-700">
                    {project.description}
                  </p>
                </div>
              ) : null}
              {project.internalNotes ? (
                <div className="sm:col-span-2 lg:col-span-4">
                  <p className="text-xs uppercase tracking-wide text-slate-500">Internal notes</p>
                  <p className="mt-1 whitespace-pre-line text-sm text-slate-700">
                    {project.internalNotes}
                  </p>
                </div>
              ) : null}
            </div>

            {/* Phases */}
            <section className="mb-6">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900">Phases</h2>
                <div className="flex items-center gap-2">
                  {canEdit ? (
                    <Button variant="secondary" onClick={openCreatePhase}>
                      Add phase
                    </Button>
                  ) : null}
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
                <DataState
                  isLoading={phasesQuery.isLoading}
                  isError={phasesQuery.isError}
                  error={phasesQuery.error}
                  onRetry={() => void phasesQuery.refetch()}
                  isEmpty={phases.length === 0}
                  emptyTitle="No phases yet"
                  emptyDescription="Break the project into phases to organize its tickets."
                  emptyAction={
                    canEdit ? <Button onClick={openCreatePhase}>Add phase</Button> : undefined
                  }
                >
                  <ul className="divide-y divide-slate-100">
                    {phases.map((phase) => {
                      const phaseTickets = ticketsByPhase.get(phase.id) ?? [];
                      return (
                        <li key={phase.id}>
                          <div
                            className={`flex items-center justify-between gap-3 px-4 py-3${
                              canEdit ? ' cursor-pointer hover:bg-slate-50' : ''
                            }`}
                            onClick={canEdit ? () => openEditPhase(phase) : undefined}
                          >
                            <div className="flex items-center gap-3">
                              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-100 text-xs font-medium text-slate-600">
                                {phase.sequence}
                              </span>
                              <span className="font-medium text-slate-900">{phase.name}</span>
                              <Badge tone={PHASE_STATUS_TONES[phase.status] ?? 'gray'}>
                                {phase.status === 'completed' ? 'Completed' : 'Active'}
                              </Badge>
                              <span className="text-xs text-slate-400">
                                {phaseTickets.length} ticket{phaseTickets.length === 1 ? '' : 's'}
                              </span>
                              <span className="text-xs text-slate-400 tabular-nums">
                                {phase.budgetHours} budget hrs
                              </span>
                              <span className="text-xs text-slate-400 tabular-nums">
                                {phase.actualHours} actual hrs
                              </span>
                              {phase.overrideServiceCategoryName ? (
                                <span className="text-xs text-slate-400">
                                  Service category override: {phase.overrideServiceCategoryName}
                                </span>
                              ) : null}
                              {phase.overrideBillingClassName ? (
                                <span className="text-xs text-slate-400">
                                  Billing class override: {phase.overrideBillingClassName}
                                </span>
                              ) : null}
                              {phase.overrideRate ? (
                                <span className="text-xs text-slate-400 tabular-nums">
                                  Override rate: {phase.overrideRate}
                                </span>
                              ) : null}
                            </div>
                            <div className="flex shrink-0 items-center gap-3">
                              {recomputeMsg?.phaseId === phase.id ? (
                                <span className="text-xs text-emerald-600">
                                  {recomputeMsg.text}
                                </span>
                              ) : null}
                              {isAdmin ? (
                                <Button
                                  variant="ghost"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleRecomputePhaseRates(phase.id);
                                  }}
                                  isLoading={
                                    recomputeRates.isPending &&
                                    recomputeRates.variables === phase.id
                                  }
                                >
                                  Recompute rates
                                </Button>
                              ) : null}
                              {canEditTickets ? (
                                <Button
                                  variant="ghost"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openCreateTicket(phase.id);
                                  }}
                                >
                                  Add ticket
                                </Button>
                              ) : null}
                            </div>
                          </div>
                          {phaseTickets.length ? (
                            <div className="border-t border-slate-100 bg-slate-50/50 pl-8">
                              <div className="border-l-2 border-slate-200">
                                {phaseTickets.map(renderTicketRow)}
                              </div>
                            </div>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                </DataState>
              </div>

              {/* Unphased project tickets */}
              {unphasedTickets.length ? (
                <div className="mt-4 rounded-xl border border-slate-200 bg-white shadow-sm">
                  <div className="border-b border-slate-200 px-4 py-3">
                    <h3 className="text-sm font-medium text-slate-700">Tickets without a phase</h3>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {unphasedTickets.map(renderTicketRow)}
                  </div>
                </div>
              ) : null}
            </section>
          </>
        ) : null}

        {tab === 'products' ? (
          <>
            {/* Products (fixed fee) */}
            <section className="mb-6">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon name="products" className="h-5 w-5 text-slate-500" />
                  <h2 className="text-lg font-semibold text-slate-900">Products (fixed fee)</h2>
                </div>
                {canEdit ? (
                  <Button variant="secondary" onClick={openCreateProduct}>
                    Attach product
                  </Button>
                ) : null}
              </div>

              <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-4 py-3 font-medium">Product</th>
                      <th className="px-4 py-3 font-medium text-right">Quantity</th>
                      <th className="px-4 py-3 font-medium text-right">Unit price</th>
                      <th className="px-4 py-3 font-medium text-right">Line total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    <DataState
                      colSpan={4}
                      isLoading={productsQuery.isLoading}
                      isError={productsQuery.isError}
                      error={productsQuery.error}
                      onRetry={() => void productsQuery.refetch()}
                      isEmpty={products.length === 0}
                      emptyTitle="No products attached"
                      emptyDescription="Attach catalog products to bill this project as fixed fee."
                      emptyAction={
                        canEdit ? (
                          <Button onClick={openCreateProduct}>Attach product</Button>
                        ) : undefined
                      }
                    >
                      {products.map((product) => (
                        <ProductRow
                          key={product.id}
                          product={product}
                          label={productLabels.get(product.productId) ?? product.productId}
                          canEdit={canEdit}
                          onEdit={() => openEditProduct(product)}
                        />
                      ))}
                    </DataState>
                  </tbody>
                  {products.length ? (
                    <tfoot>
                      <tr className="border-t border-slate-200">
                        <td className="px-4 py-3 text-sm font-semibold text-slate-900" colSpan={3}>
                          Fixed-fee total
                        </td>
                        <td className="px-4 py-3 text-right text-sm font-semibold text-slate-900">
                          {formatMoney(fixedFeeTotal)}
                        </td>
                      </tr>
                    </tfoot>
                  ) : null}
                </table>
              </div>
            </section>
          </>
        ) : null}

        <ProjectFormModal isOpen={editOpen} project={project} onClose={() => setEditOpen(false)} />
        <PhaseFormModal
          isOpen={phaseModalOpen}
          projectId={id}
          phase={editingPhase}
          nextSequence={nextSequence}
          onClose={() => setPhaseModalOpen(false)}
        />
        <ProjectProductFormModal
          isOpen={productModalOpen}
          projectId={id}
          projectProduct={editingProduct}
          attachedProductIds={attachedProductIds}
          onClose={() => setProductModalOpen(false)}
        />
        {canEditTickets ? (
          <TicketFormModal
            isOpen={ticketModalOpen}
            ticket={editingTicket}
            initialProjectId={id}
            initialPhaseId={ticketInitialPhaseId}
            onClose={() => setTicketModalOpen(false)}
          />
        ) : null}
        <ConfirmDialog
          isOpen={regenerateOpen}
          title="Regenerate public link"
          message="Generate a new public link for this project? The current link will stop working immediately and anyone using it will need the new one."
          confirmLabel="Regenerate link"
          destructive
          isConfirming={regenerateLink.isPending}
          onConfirm={() => {
            regenerateLink.mutate(id, { onSuccess: () => setRegenerateOpen(false) });
          }}
          onCancel={() => setRegenerateOpen(false)}
        />
        <ConfirmDialog
          isOpen={closeOpen}
          title="Close project & mark Ready to Bill"
          message="This will close this project and notify the team to start billing for the product, are you sure?"
          confirmLabel="Yes, close project"
          destructive
          isConfirming={closeReadyToBill.isPending}
          onConfirm={() => {
            closeReadyToBill.mutate(id, {
              onSuccess: (res) => {
                setCloseOpen(false);
                setCloseNotice(
                  res.notificationSent
                    ? { tone: 'ok', text: 'Project closed and the billing team was notified.' }
                    : {
                        tone: 'warn',
                        text: `Project closed, but no notification was sent. ${
                          res.notificationSkippedReason ?? ''
                        }`.trim(),
                      },
                );
              },
            });
          }}
          onCancel={() => setCloseOpen(false)}
        />
      </div>
    </div>
  );
}

interface ProductRowProps {
  product: ProjectProduct;
  label: string;
  canEdit: boolean;
  onEdit: () => void;
}

function ProductRow({ product, label, canEdit, onEdit }: ProductRowProps): JSX.Element {
  return (
    <tr
      className={canEdit ? 'cursor-pointer hover:bg-slate-50' : ''}
      onClick={canEdit ? onEdit : undefined}
    >
      <td className="px-4 py-3 text-slate-900">{label}</td>
      <td className="px-4 py-3 text-right text-slate-600">{product.quantity}</td>
      <td className="px-4 py-3 text-right text-slate-600">
        {formatMoney(Number(product.unitPrice))}
      </td>
      <td className="px-4 py-3 text-right font-medium text-slate-900">
        {formatMoney(lineTotal(product))}
      </td>
    </tr>
  );
}
